/**
 * Design tokens for the marketing landing page.
 *
 * The landing page is art-directed independently of the in-app theme: it only
 * borrows light/dark polarity, then uses the brand's neutral palette so the
 * page reads as one composed piece across all ten app themes.
 *
 * Type is fluid rather than breakpoint-stepped, and tracking is expressed as a
 * ratio of font size — large display sizes need far tighter tracking than body
 * copy, and a fixed pixel value can only be correct at one size.
 */
import { Platform } from 'react-native';
import publicTypography from '../../../shared/public/typography.cjs';
import { Themes } from '@/constants/Colors';

export const isWeb = Platform.OS === 'web';
export const PUBLIC_FONT_FAMILY = publicTypography.fontFamily;

/**
 * Brand accents are monochrome. Use the theme-aware landing palette for
 * controls so dark appearance also reverses their foreground color.
 */
export const ACCENT = '#040404';
export const ACCENT_BRIGHT = '#616161';
export const ACCENT_TINT = '#FFFFFF';
export const ON_ACCENT = '#FFFFFF';

/** Use the user-selected app accent when present; otherwise keep the marketing accent. */
export function resolveUserAccent(accentId, accentColorPrimary) {
  if (accentId && accentId !== 'multicolor') return accentColorPrimary || ACCENT;
  return ACCENT;
}

const ACCENT_RGB = '4,4,4';
const ACCENT_TINT_RGB = '255,255,255';

/** Translucent neutral for selection surfaces and borders. */
export function accentAlpha(alpha, dark = false) {
  return `rgba(${dark ? ACCENT_TINT_RGB : ACCENT_RGB},${alpha})`;
}

/**
 * Full-bleed bands are the page's structural accent: a recessed surface that
 * separates one movement from the next. They follow the theme rather than
 * always being dark, so a light page never drops into an unexplained black box.
 * A flat neutral surface separates the band from the surrounding canvas.
 */
export function landingBand(isDark) {
  if (isDark) {
    return {
      bg: '#1C1C1C',
      ink: '#FFFFFF',
      inkSoft: 'rgba(255,255,255,0.78)',
      muted: '#B3B3B3',
      faint: 'rgba(255,255,255,0.40)',
      rule: '#383838',
      surface: 'rgba(255,255,255,0.05)',
      surfaceSoft: 'rgba(255,255,255,0.04)',
    };
  }
  return {
    bg: '#F5F5F5',
    ink: '#040404',
    inkSoft: '#616161',
    muted: '#616161',
    faint: 'rgba(4,4,4,0.38)',
    rule: '#E5E5E5',
    surface: '#FFFFFF',
    surfaceSoft: 'rgba(255,255,255,0.66)',
  };
}

/**
 * `ui-serif` resolves to New York on Apple platforms, which is a far better
 * editorial face than Georgia. Georgia stays last as the universal fallback.
 */
export const SERIF = "ui-serif, 'New York', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif";
export const MONO = "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace";

export const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
export const EASE_OUT = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

/** Viewport range across which fluid type and spacing interpolate. */
const FLUID_MIN_WIDTH = 390;
const FLUID_MAX_WIDTH = 1440;

/** Linearly interpolate a value between two viewport widths, clamped at both ends. */
export function fluid(width, min, max, minWidth = FLUID_MIN_WIDTH, maxWidth = FLUID_MAX_WIDTH) {
  if (!Number.isFinite(width) || width <= minWidth) return min;
  if (width >= maxWidth) return max;
  const t = (width - minWidth) / (maxWidth - minWidth);
  return Math.round((min + (max - min) * t) * 2) / 2;
}

/**
 * Build a display type style. `leading` is a multiple of font size and
 * `tracking` is an em ratio, so the optical result holds at every size.
 */
export function displayType(width, { min, max, leading = 0.98, tracking = -0.04 }) {
  const fontSize = fluid(width, min, max);
  return {
    fontSize,
    lineHeight: Math.round(fontSize * leading),
    letterSpacing: Math.round(fontSize * tracking * 10) / 10,
  };
}

/** Body copy scale: looser leading, near-neutral tracking. */
export function proseType(width, { min, max, leading = 1.5, tracking = -0.011 }) {
  const fontSize = fluid(width, min, max);
  return {
    fontSize,
    lineHeight: Math.round(fontSize * leading),
    letterSpacing: Math.round(fontSize * tracking * 10) / 10,
  };
}

/** Small all-caps labels. Positive tracking is what makes them read as labels. */
export function labelType(size = 11, tracking = 0.16) {
  return {
    fontSize: size,
    lineHeight: Math.round(size * 1.4),
    letterSpacing: Math.round(size * tracking * 10) / 10,
    textTransform: 'uppercase',
  };
}

/**
 * The brand's black, white, and equal-channel gray palette.
 *
 * `accent` is safe for text and icons on the page surface; `accentFill` is
 * for solid buttons and progress bars. `onAccent` reverses with the fill.
 */
export function landingPalette(isDark) {
  if (isDark) {
    return {
      isDark: true,
      page: '#040404',
      ink: '#FFFFFF',
      inkSoft: 'rgba(255,255,255,0.78)',
      muted: '#B3B3B3',
      faint: 'rgba(255,255,255,0.34)',
      rule: '#383838',
      ruleStrong: '#808080',
      panel: 'rgba(255,255,255,0.035)',
      card: 'rgba(255,255,255,0.045)',
      cardSolid: '#1C1C1C',
      accent: ACCENT_TINT,
      accentFill: '#FFFFFF',
      accentHover: '#E5E5E5',
      onAccent: '#040404',
      accentWash: accentAlpha(0.14, true),
      accentSoft: accentAlpha(0.20, true),
      accentRing: accentAlpha(0.42, true),
      // Shadows are for separation, not levitation: one tight contact line
      // plus a short, low-alpha ambient. Borders do most of the work.
      shadow: '0 1px 0 rgba(0,0,0,0.5), 0 12px 32px -8px rgba(0,0,0,0.5)',
      shadowSoft: '0 1px 0 rgba(0,0,0,0.45), 0 6px 18px -6px rgba(0,0,0,0.4)',
    };
  }
  return {
    isDark: false,
    page: '#FFFFFF',
    ink: '#040404',
    inkSoft: '#616161',
    muted: '#616161',
    faint: 'rgba(4,4,4,0.34)',
    rule: '#E5E5E5',
    ruleStrong: '#808080',
    panel: '#F5F5F5',
    card: '#FFFFFF',
    cardSolid: '#FFFFFF',
    accent: ACCENT,
    accentFill: ACCENT,
    accentHover: '#282828',
    onAccent: '#FFFFFF',
    accentWash: accentAlpha(0.08),
    accentSoft: accentAlpha(0.12),
    accentRing: accentAlpha(0.38),
    shadow: '0 1px 2px rgba(0,0,0,0.06), 0 12px 32px -8px rgba(0,0,0,0.10)',
    shadowSoft: '0 1px 2px rgba(0,0,0,0.05), 0 6px 18px -6px rgba(0,0,0,0.08)',
  };
}

/** Adapt the existing app theme contract to the shared marketing primitives. */
export function appLandingPalette(colors = {}, isDark = false, theme) {
  const base = landingPalette(isDark);
  const white = theme === 'white' || (!theme && !isDark && colors.solid_color_primary === Themes.white.solid_color_primary);
  return {
    ...base,
    page: colors.solid_color_primary || base.page,
    panel: white ? '#F7F7F8' : colors.solid_color_secondary || base.panel,
    card: white ? '#FAFAFA' : colors.solid_color_tertiary || base.card,
    cardSolid: white ? '#FAFAFA' : colors.solid_color_block || colors.solid_color_primary || base.cardSolid,
    hover: white ? '#F0F0F2' : colors.solid_color_hover || colors.solid_color_tertiary || base.panel,
    ink: colors.text_color_primary || base.ink,
    inkSoft: colors.text_color_secondary || base.inkSoft,
    muted: colors.text_color_secondary || base.muted,
    faint: colors.text_color_tertiary || base.faint,
    rule: white ? '#e6e6e7' : colors.border_color || colors.border_color_secondary || base.rule,
    ruleStrong: white ? '#b0b2b6' : colors.border_color_primary || colors.border_color_input || base.ruleStrong,
    accent: base.accent,
    accentFill: base.accentFill,
    accentHover: base.accentHover,
    onAccent: base.onAccent,
    accentWash: white ? '#F7F7F8' : colors.solid_color_secondary || base.accentWash,
    accentSoft: white ? '#E8E8EB' : colors.solid_color_hover || colors.solid_color_secondary || base.accentSoft,
    accentRing: white ? '#b0b2b6' : colors.border_color_primary || colors.border_color_input || base.accentRing,
    // Decorative lighting preserves the blue/lavender compositions separately
    // from functional controls, while its brightness follows the page theme.
    previewAccent: colors.solid_color_bubble_blue || Themes.white.accent_color_primary,
    previewCompanion: Themes.pastel.accent_color_primary,
  };
}

/** One palette for CSS product previews and the surrounding RN components. */
export function landingCssVariables(c) {
  return {
    '--page-bg': c.page, '--surface': c.cardSolid, '--panel': c.panel,
    '--hover': c.hover || c.panel, '--text': c.ink, '--muted': c.muted,
    '--border': c.rule, '--border-strong': c.ruleStrong,
    '--selected-bg': c.accentSoft, '--selected-border': c.accentRing,
    '--accent': c.accent, '--accent-fill': c.accentFill, '--on-accent': c.onAccent,
    '--focus': c.ink, '--shadow': c.shadow, '--shadow-soft': c.shadowSoft,
    '--font-family': PUBLIC_FONT_FAMILY,
    '--preview-accent': c.previewAccent || Themes.white.accent_color_primary,
    '--preview-companion': c.previewCompanion || Themes.pastel.accent_color_primary,
    '--preview-glow-1': `color-mix(in srgb, var(--preview-accent) ${c.isDark ? 36 : 34}%, var(--page-bg))`,
    '--preview-glow-2': `color-mix(in srgb, var(--preview-companion) ${c.isDark ? 30 : 36}%, var(--page-bg))`,
    '--preview-glow-3': 'color-mix(in srgb, var(--accent-fill) 12%, var(--surface))',
    '--preview-gradient': 'radial-gradient(ellipse at 5% 8%, var(--preview-glow-1), transparent 67%), radial-gradient(ellipse at 97% 12%, var(--preview-glow-2), transparent 60%), radial-gradient(ellipse at 35% 100%, var(--preview-glow-3), transparent 66%)',
  };
}

/** Content measure and gutters. `wide` is for full-bleed bands, `text` caps line length. */
export const LAYOUT = {
  max: 1280,
  wide: 1440,
  text: 620,
};

export function sidePadding(width) {
  return fluid(width, 20, 64);
}

/**
 * Vertical rhythm between major sections. Sections pad top and bottom, so the
 * gap between two of them is twice this value.
 */
export function sectionSpace(width) {
  return fluid(width, 66, 104);
}

export function prefersReducedMotion() {
  if (!isWeb || typeof window === 'undefined') return false;
  return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
}
